import { FC } from "react";

import { ArrowLeftIcon, ArrowRightIcon } from "../../../../assets/img/icons";
import Button from "../../../../components/Button";
import IconButton from "../../../../components/IconButton";
import Text from "../../../../components/Text";

import Stat from "../../../../components/Text/Stat";
import { useJournalSettings } from "../../../../services/journalSettings";
import { NoTradesSession } from "../../../../types/NoTradesSession";
import { Session } from "../../../../types/Session";
import { parseInputDateToLocal } from "../../../../utils/date";
import styles from "./styles.module.scss";

type Props = {
    sessions: {
        [key: string]: (Session | NoTradesSession)[];
    };
    hidePerformance: boolean;
    currentMonth: Date;
    setCurrentMonth: (date: Date) => void;
};

const CalendarHeader: FC<Props> = ({
    sessions,
    hidePerformance,
    currentMonth,
    setCurrentMonth,
}) => {
    const { challengeMoneyManagement } = useJournalSettings();

    const onPrevMonth = () => {
        const prev = new Date(currentMonth);
        prev.setMonth(prev.getMonth() - 1);
        setCurrentMonth(prev);
    };

    const onNextMonth = () => {
        const next = new Date(currentMonth);
        next.setMonth(next.getMonth() + 1);
        setCurrentMonth(next);
    };

    const getSessionsNumber = () => {
        // Only count days that are in the current month AND have trading sessions
        const currentYear = currentMonth.getFullYear();
        const currentMonthIndex = currentMonth.getMonth();

        return Object.keys(sessions).filter((dateString) => {
            const date = parseInputDateToLocal(dateString);
            if (!date) return false;

            // Check if the date belongs to the current month
            const isInCurrentMonth =
                date.getFullYear() === currentYear &&
                date.getMonth() === currentMonthIndex;

            if (!isInCurrentMonth) return false;

            // Only count days that have sessions (exclude empty arrays)
            const daySessions = sessions[dateString];
            return daySessions && daySessions.length > 0;
        }).length;
    };

    const getMonthTotal = () => {
        // Only sum totals from days that are in the current month
        const currentYear = currentMonth.getFullYear();
        const currentMonthIndex = currentMonth.getMonth();
        const roundCurrency = (value: number) =>
            Math.round((value + Number.EPSILON) * 100) / 100;

        let total = 0;

        Object.entries(sessions).forEach(([dateString, daySessions]) => {
            const date = parseInputDateToLocal(dateString);
            if (!date) return;

            // Only count sessions from days in the current month
            if (
                date.getFullYear() === currentYear &&
                date.getMonth() === currentMonthIndex
            ) {
                const dayTotal = daySessions
                    .filter((session) => {
                        return (
                            (session as Session).moneyManagement !==
                            challengeMoneyManagement
                        );
                    })
                    .reduce(
                        (acc, session) =>
                            roundCurrency(acc + (session.total || 0)),
                        0,
                    );
                total = roundCurrency(total + dayTotal);
            }
        });

        return total;
    };

    const perfTotal = getMonthTotal();
    const dayCount = getSessionsNumber();

    return (
        <div className={styles.calendarHeader}>
            <div className={styles.monthSelectorContainer}>
                <div className={styles.monthSelector}>
                    <IconButton
                        icon={ArrowLeftIcon}
                        size="sx"
                        onClick={onPrevMonth}
                    />
                    <Text variant="body-bold" className={styles.currentMonth}>
                        {currentMonth.toLocaleString("default", {
                            month: "long",
                            year: "numeric",
                        })}
                    </Text>
                    <IconButton
                        icon={ArrowRightIcon}
                        size="sx"
                        onClick={onNextMonth}
                    />
                </div>
                <div className={styles.buttonsContainer}>
                    <Button
                        size="newSmall"
                        variant="secondary"
                        onClick={() => setCurrentMonth(new Date())}
                    >
                        Ce mois
                    </Button>
                </div>
            </div>
            {!hidePerformance && (
                <div className={styles.mensualStats}>
                    <Text variant="body-bold" className={styles.perfSummary}>
                        Depuis le 1er :{" "}
                        <Stat variant="body-bold" type="currency">
                            {perfTotal}
                        </Stat>{" "}
                        sur {dayCount} j.
                    </Text>
                </div>
            )}
        </div>
    );
};

export default CalendarHeader;
